import React, { useState } from 'react';
import { NameEntry } from '../components/NameEntry';
import { BruteScoreEntry } from '../components/BruteScoreEntry';
import { SwipeTest } from '../components/SwipeTest';
import { ResultsScreen } from '../components/ResultsScreen';
import { TestStep, TestResults } from '../types';
import { calculateNerdScore } from '../utils/scoring';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, ZapIcon } from 'lucide-react';
import { sendToWebhooks } from '../hooks/useWebhooks';
interface TestFlowProps {
  onComplete: () => void;
  onHome: () => void;
  onViewLeaderboard: (name: string) => void;
}
// LetLois icon for practice cards
function LetLoisBadge() {
  return <div className="rounded-xl flex items-center justify-center flex-shrink-0" style={{
    width: 32,
    height: 32,
    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)'
  }} data-id="element-482">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" data-id="element-483">
        <path d="M13 2L4.5 13.5H11L10 22L19.5 10.5H13L13 2Z" fill="white" stroke="white" strokeWidth="0.5" strokeLinejoin="round" data-id="element-484" />
      </svg>
    </div>;
}
// App icons for practice — matching the real test
function PracticeAppIcon({
  type
}: {
  type: 'letlois' | 'slack' | 'google' | 'phone';
}) {
  const s = 32;
  if (type === 'letlois') return <LetLoisBadge data-id="element-485" />;
  if (type === 'slack') {
    return <div className="rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{
      width: s,
      height: s,
      background: '#4A154B',
      borderRadius: 7
    }} data-id="element-486">
        <svg viewBox="0 0 24 24" width={19} height={19} fill="none" data-id="element-487">
          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="white" data-id="element-488" />
        </svg>
      </div>;
  }
  if (type === 'google') {
    return <div className="rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{
      width: s,
      height: s,
      background: 'white',
      borderRadius: 7
    }} data-id="element-489">
        <svg viewBox="0 0 24 24" width={20} height={20} data-id="element-490">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-491" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-492" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-493" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-494" />
        </svg>
      </div>;
  }
  // phone
  return <div className="rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{
    width: s,
    height: s,
    background: '#1C1C1E',
    borderRadius: 7
  }} data-id="element-495">
      <svg viewBox="0 0 24 24" width={19} height={19} fill="#FF3B30" data-id="element-496">
        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" data-id="element-497" />
      </svg>
    </div>;
}
const PRACTICE_CARDS = [{
  id: 'p1',
  appName: 'LetLois',
  appIcon: 'letlois' as const,
  isLetLois: true,
  time: 'now',
  title: '⚡ New Lead — Jessica M.',
  body: 'Hey! I want to book. What do you have open?',
  correctAnswer: 'keep' as const,
  hint: 'Hot lead — KEEP 🔥'
}, {
  id: 'p2',
  appName: 'Slack',
  appIcon: 'slack' as const,
  isLetLois: false,
  time: '7:02 AM',
  title: 'Riley in #team-chat',
  body: "Don't feel like coming in today lol",
  correctAnswer: 'toss' as const,
  hint: 'Bad behavior — TOSS 🗑️'
}, {
  id: 'p3',
  appName: 'Google Reviews',
  appIcon: 'google' as const,
  isLetLois: false,
  time: '5m ago',
  title: 'Sarah M. left a 5-star review ⭐⭐⭐⭐⭐',
  body: 'Incredible experience. Will 100% be back!',
  correctAnswer: 'keep' as const,
  hint: '5-star review — KEEP ✅'
}, {
  id: 'p4',
  appName: 'Phone',
  appIcon: 'phone' as const,
  isLetLois: false,
  time: '11m ago',
  title: 'Missed Call + Voicemail',
  body: '"Ready to book and pay today. Call me back!"',
  correctAnswer: 'keep' as const,
  hint: 'Ready buyer — KEEP 💰'
}];
export function TestFlow({
  onComplete,
  onHome,
  onViewLeaderboard
}: TestFlowProps) {
  const [step, setStep] = useState<TestStep>('name');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [photo, setPhoto] = useState('');
  const [bruteScore, setBruteScore] = useState(0);
  const [bruteEntryId, setBruteEntryId] = useState<string | null>(null);
  const [swipeCorrect, setSwipeCorrect] = useState(0);
  const [swipeTotal, setSwipeTotal] = useState(0);
  const [nerdTimeSeconds, setNerdTimeSeconds] = useState(0);
  const [practiceUnlocked, setPracticeUnlocked] = useState(false);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [practiceRounds, setPracticeRounds] = useState(0);
  const [practiceResult, setPracticeResult] = useState<'correct' | 'wrong' | null>(null);
  const [showStartNudge, setShowStartNudge] = useState(false);
  const {
    addEntry,
    updateEntry
  } = useLeaderboard();
  const fullName = `${firstName} ${lastName}`.trim();
  const handleNameSubmit = (fn: string, ln: string, p: string) => {
    setFirstName(fn);
    setLastName(ln);
    setPhoto(p);
    setStep('brute');
  };
  const handleBruteSubmit = (score: number) => {
    setBruteScore(score);
    if (bruteEntryId) {
      updateEntry(bruteEntryId, {
        score,
        name: fullName,
        firstName,
        lastName,
        details: {
          grip: score
        }
      });
    } else {
      const id = addEntry({
        name: fullName,
        firstName,
        lastName,
        score,
        type: 'brawn',
        details: {
          grip: score
        }
      });
      setBruteEntryId(id);
    }
    setStep('nerd-intro');
  };
  const handlePracticeDecision = (decision: 'keep' | 'toss') => {
    if (practiceResult !== null) return;
    const card = PRACTICE_CARDS[practiceIndex];
    const isCorrect = decision === card.correctAnswer;
    setPracticeResult(isCorrect ? 'correct' : 'wrong');
    setTimeout(() => {
      const nextIndex = (practiceIndex + 1) % PRACTICE_CARDS.length;
      const newRounds = practiceRounds + 1;
      setPracticeIndex(nextIndex);
      setPracticeRounds(newRounds);
      setPracticeResult(null);
      if (newRounds >= 3) setShowStartNudge(true);
    }, 600);
  };
  const handleSwipeComplete = (correct: number, total: number, timeSeconds: number) => {
    setSwipeCorrect(correct);
    setSwipeTotal(total);
    setNerdTimeSeconds(timeSeconds);
    setStep('calculating');
    setTimeout(async () => {
      const finalNerdScore = calculateNerdScore(timeSeconds);
      addEntry({
        name: fullName,
        firstName,
        lastName,
        score: finalNerdScore,
        type: 'brains',
        details: {
          swipe: correct,
          swipeTotal: total,
          nerdTimeSeconds: timeSeconds
        }
      });
      try {
        await sendToWebhooks({
          firstName,
          lastName,
          fullName,
          photo: photo || null,
          bruteScore,
          nerdTimeSeconds: timeSeconds,
          swipeCorrect: correct,
          swipeTotal: total,
          timestamp: new Date().toISOString()
        });
      } catch (e) {}
      setStep('results');
    }, 1500);
  };
  const BackButton = ({
    onClick
  }: {
    onClick: () => void;
  }) => <button onClick={onClick} className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors text-sm" data-id="element-498">
      <ArrowLeftIcon className="w-4 h-4" data-id="element-499" /> Back
    </button>;
  if (step === 'name') {
    return <div className="min-h-screen bg-zinc-950 p-4 pt-8" data-id="element-500">
        <BackButton onClick={onHome} data-id="element-501" />
        <NameEntry onNext={handleNameSubmit} data-id="element-502" />
      </div>;
  }
  if (step === 'brute') {
    return <div className="relative" data-id="element-503">
        <div className="absolute top-4 left-4 z-10" data-id="element-504">
          <BackButton onClick={() => setStep('name')} data-id="element-505" />
        </div>
        <BruteScoreEntry onNext={handleBruteSubmit} initialScore={bruteScore > 0 ? bruteScore : undefined} data-id="element-506" />
      </div>;
  }
  if (step === 'nerd-intro') {
    const practiceCard = PRACTICE_CARDS[practiceIndex];
    const isAnswered = practiceResult !== null;
    return <div className="min-h-screen bg-zinc-950 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-zinc-950 to-zinc-950 flex flex-col items-center overflow-y-auto" data-id="element-507">
        {/* Back */}
        <div className="w-full max-w-sm px-4 pt-6 pb-2 flex" data-id="element-508">
          <BackButton onClick={() => setStep('brute')} data-id="element-509" />
        </div>

        {/* Title — font-mono to match opening slide, big and bold */}
        <div className="text-center px-4 mb-5" data-id="element-510">
          <h1 className="text-6xl md:text-8xl font-mono font-bold text-white tracking-tight uppercase leading-none" data-id="element-511">
            NERD <span className="text-cyan-500" data-id="element-512">TEST</span>
          </h1>
          <p className="text-gray-400 text-sm uppercase tracking-widest mt-2 font-bold" data-id="element-513">
            6 notifications · Finish as fast as possible
          </p>
          <p className="text-gray-500 text-xs mt-1" data-id="element-514">
            Review each notification like a real business owner
          </p>
        </div>

        {/* Practice zone */}
        <div className="w-full max-w-sm px-4" data-id="element-515">
          <div className="relative border-2 border-dashed border-yellow-500/50 rounded-2xl p-4" data-id="element-516">
            {/* Practice badge */}
            <div className="flex justify-center mb-3" data-id="element-517">
              <div className="flex items-center gap-2 bg-yellow-500/15 border border-yellow-500/40 rounded-full px-4 py-1.5" data-id="element-518">
                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" data-id="element-519" />
                <span className="text-yellow-400 text-xs font-black uppercase tracking-widest" data-id="element-520">
                  Practice Area
                </span>
                {practiceRounds > 0 && <span className="text-yellow-600 text-xs font-bold" data-id="element-521">
                    · {practiceRounds} done
                  </span>}
              </div>
            </div>

            {/* 2-step instructions */}
            <div className="flex gap-2 mb-3" data-id="element-522">
              <div className="flex-1 bg-zinc-800/60 rounded-lg p-2.5 border border-zinc-700/50 text-center" data-id="element-523">
                <p className="text-white text-xs font-bold" data-id="element-524">1. Who is it?</p>
                <p className="text-gray-500 text-xs mt-0.5" data-id="element-525">
                  Team · Lead · Customer
                </p>
              </div>
              <div className="flex-1 bg-zinc-800/60 rounded-lg p-2.5 border border-zinc-700/50 text-center" data-id="element-526">
                <p className="text-white text-xs font-bold" data-id="element-527">2. Keep or Toss?</p>
                <p className="text-gray-500 text-xs mt-0.5" data-id="element-528">
                  Good news or bad?
                </p>
              </div>
            </div>

            {/* Practice card + buttons */}
            <div className="relative" data-id="element-529">
              {/* Blur overlay — only before first unlock */}
              {!practiceUnlocked && <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl bg-zinc-950/70 backdrop-blur-sm" data-id="element-530">
                  <button onClick={() => setPracticeUnlocked(true)} className="bg-yellow-500 hover:bg-yellow-400 active:scale-95 text-black font-black uppercase tracking-wider text-base px-8 py-3 rounded-lg transition-all shadow-[0_0_24px_rgba(234,179,8,0.5)]" data-id="element-531">
                    👆 PRACTICE
                  </button>
                  <p className="text-gray-400 text-xs mt-2" data-id="element-532">
                    Try it before your real test
                  </p>
                </div>}

              <div className={!practiceUnlocked ? 'blur-sm pointer-events-none select-none' : ''} data-id="element-533">
                {/* Notification-style practice card */}
                <div className={`rounded-2xl overflow-hidden border-2 transition-all duration-300 ${practiceResult === 'correct' ? 'border-green-500 shadow-[0_0_16px_rgba(34,197,94,0.3)]' : practiceResult === 'wrong' ? 'border-red-500 shadow-[0_0_16px_rgba(239,68,68,0.3)]' : 'border-zinc-700'}`} style={{
                background: 'rgba(28,28,30,0.95)'
              }} data-id="element-534">
                  {/* App header */}
                  <div className="flex items-center gap-2.5 px-4 pt-3 pb-2" data-id="element-535">
                    <PracticeAppIcon type={practiceCard.appIcon} data-id="element-536" />
                    <div className="flex-1 flex items-center justify-between" data-id="element-537">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${practiceCard.isLetLois ? 'text-violet-400' : 'text-gray-400'}`} data-id="element-538">
                        {practiceCard.appName}
                      </span>
                      <span className="text-gray-500 text-xs" data-id="element-539">
                        {practiceCard.time}
                      </span>
                    </div>
                  </div>
                  <div className="h-px bg-zinc-700/50 mx-4" data-id="element-540" />
                  <div className="px-4 py-3" data-id="element-541">
                    <p className={`font-semibold text-sm mb-1 ${practiceCard.isLetLois ? 'text-violet-200' : 'text-white'}`} data-id="element-542">
                      {practiceCard.title}
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed" data-id="element-543">
                      {practiceCard.body}
                    </p>
                    {practiceCard.isLetLois && <div className="mt-2 flex items-center gap-1.5 bg-violet-900/30 rounded-lg px-3 py-1.5 border border-violet-700/30" data-id="element-544">
                        <LetLoisBadge data-id="element-545" />
                        <span className="text-violet-300 text-xs font-bold" data-id="element-546">
                          LetLois · Tap to respond
                        </span>
                      </div>}
                  </div>
                  {isAnswered && <div className={`px-4 py-2.5 flex items-center gap-2 ${practiceResult === 'correct' ? 'bg-green-900/30' : 'bg-red-900/30'}`} data-id="element-547">
                      {practiceResult === 'correct' ? <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-548" /> : <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-549" />}
                      <p className={`text-sm font-bold ${practiceResult === 'correct' ? 'text-green-400' : 'text-red-400'}`} data-id="element-550">
                        {practiceResult === 'correct' ? '✓ Correct! ' : '✗ Not quite. '}
                        <span className="font-normal text-gray-300" data-id="element-551">
                          {practiceCard.hint}
                        </span>
                      </p>
                    </div>}
                </div>

                {/* TOSS / KEEP buttons */}
                <div className="flex gap-3 mt-3" data-id="element-552">
                  <button onClick={() => handlePracticeDecision('toss')} disabled={isAnswered} className="flex-1 py-4 rounded-lg bg-red-500/20 border-2 border-red-500 text-red-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-all disabled:opacity-40" data-id="element-553">
                    👈 TOSS
                  </button>
                  <button onClick={() => handlePracticeDecision('keep')} disabled={isAnswered} className="flex-1 py-4 rounded-lg bg-green-500/20 border-2 border-green-500 text-green-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-all disabled:opacity-40" data-id="element-554">
                    KEEP 👉
                  </button>
                </div>
              </div>
            </div>

            {/* Nudge after 3 rounds */}
            {showStartNudge && <div className="mt-3 flex items-center gap-2 bg-cyan-900/20 border border-cyan-700/40 rounded-lg px-4 py-2.5" data-id="element-555">
                <ZapIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" data-id="element-556" />
                <p className="text-cyan-300 text-sm font-bold" data-id="element-557">
                  You've got it! Ready for the real thing?
                </p>
              </div>}
          </div>

          {/* START TEST button */}
          <button onClick={() => setStep('swipe')} className="w-full mt-5 mb-8 bg-cyan-600 hover:bg-cyan-500 active:scale-95 text-white font-black uppercase tracking-wider text-xl py-5 rounded-sm transition-all shadow-[0_0_24px_rgba(6,182,212,0.3)]" data-id="element-558">
            START TEST NOW ⚡
          </button>
        </div>
      </div>;
  }
  if (step === 'swipe') {
    return <div className="min-h-screen bg-zinc-950 p-4 pt-6 flex flex-col" data-id="element-559">
        <SwipeTest onComplete={handleSwipeComplete} data-id="element-560" />
      </div>;
  }
  if (step === 'calculating') {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-id="element-561">
        <div className="text-center" data-id="element-562">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-563" />
          <h2 className="text-2xl font-mono text-cyan-500 animate-pulse" data-id="element-564">
            CALCULATING SCORES...
          </h2>
        </div>
      </div>;
  }
  if (step === 'results') {
    const results: TestResults = {
      firstName,
      lastName,
      photo,
      bruteScore,
      swipeCorrect,
      swipeTotal,
      nerdTimeSeconds,
      totalNerdScore: calculateNerdScore(nerdTimeSeconds)
    };
    return <ResultsScreen results={results} onHome={onHome} onViewLeaderboard={() => onViewLeaderboard(fullName)} onChallengeAgain={() => {
      setStep('name');
      setBruteScore(0);
      setBruteEntryId(null);
      setSwipeCorrect(0);
      setSwipeTotal(0);
      setNerdTimeSeconds(0);
      setPracticeUnlocked(false);
      setPracticeRounds(0);
      setPracticeIndex(0);
      setShowStartNudge(false);
    }} data-id="element-565" />;
  }
  return null;
}